import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { customerCategoriesService } from '../services/customerCategoriesService';
import AddPropertyModal from '../components/AddPropertyModal';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import { TitleContext } from '../components/Topbar';
import { ArrowLeft, Plus, Trash2, Save, X, Edit } from 'lucide-react';

interface CategoryEntry {
  key: string;
  value: any;
}

const CreateCategory: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, setUserAndToken } = useUser();
  const { setDynamicTitle } = useContext(TitleContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_default: false
  });
  
  const [entries, setEntries] = useState<CategoryEntry[]>([]);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<any>(null);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Set dynamic title when component mounts
  useEffect(() => {
    setDynamicTitle('Create Customer Category');
    
    // Clean up title when component unmounts
    return () => {
      setDynamicTitle(null);
    };
  }, [setDynamicTitle]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Convert display name to API key (e.g., "Main Concerns" -> "main_concerns")
  const convertToApiKey = (displayName: string): string => {
    return displayName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters but keep spaces
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .trim();
  };

  // Convert nested key to API format (e.g., "main_concern.MC1" -> "main_concern.mc1")
  const convertNestedKeyToApi = (parentKey: string, childKey: string): string => {
    const apiParentKey = convertToApiKey(parentKey);
    const apiChildKey = convertToApiKey(childKey);
    return `${apiParentKey}.${apiChildKey}`;
  };

  const startEditing = (key: string, value: any) => {
    setEditingField(key);
    // Create a shallow copy to avoid mutating original while typing
    if (Array.isArray(value)) {
      setEditValue([...(value as any[])]);
    } else if (typeof value === 'object' && value !== null) {
      setEditValue({ ...(value as Record<string, any>) });
    } else {
      setEditValue(value);
    }
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue(null);
  };

  const handleAddProperty = (property: { key: string; value: any }) => {
    console.log('Modal sent key:', property.key);
    const newEntry: CategoryEntry = {
      key: property.key,
      value: property.value
    };
    
    setEntries(prev => [newEntry, ...prev]);
  };


  const updatePropertyName = (oldKey: string, newKey: string) => {
    const apiKey = convertToApiKey(newKey);
    
    setEntries(prev => prev.map(entry => 
      entry.key === oldKey ? { ...entry, key: apiKey } : entry
    ));
    
    // Update editing field if currently editing this property
    if (editingField === oldKey) {
      setEditingField(apiKey);
    }
  };

  const updatePropertyType = (key: string, newType: 'value' | 'object' | 'array') => {
    let defaultValue: any;
    switch (newType) {
      case 'value': 
        defaultValue = ''; 
        break;
      case 'object': 
        defaultValue = { '': '' }; 
        break;
      case 'array': 
        defaultValue = ['']; 
        break;
    }
    
    setEntries(prev => prev.map(entry => 
      entry.key === key ? { ...entry, value: defaultValue } : entry
    ));
    
    // Update editing value if currently editing this property
    if (editingField === key) {
      setEditValue(defaultValue);
    }
  };

  const removeEntry = (key: string) => {
    setDeleteTarget(key);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      setEntries(prev => prev.filter(entry => entry.key !== deleteTarget));
      setDeleteTarget(null);
    }
    setShowDeleteModal(false);
  };

  const cancelDelete = () => {
    setDeleteTarget(null);
    setShowDeleteModal(false);
  };

  const updateFormValue = (key: string, value: any) => {
    setEditValue(value);
  };

  const updateObjectValue = (parentKey: string, childKey: string, value: string) => {
    if (editingField !== parentKey) return;
    setEditValue((prev: any) => ({ ...(prev || {}), [childKey]: value }));
  };

  const removeObjectKey = (parentKey: string, childKey: string) => {
    if (editingField !== parentKey) return;
    setEditValue((prev: any) => {
      const copy = { ...(prev || {}) } as Record<string, any>;
      delete copy[childKey];
      return copy;
    });
  };

  const addObjectKey = (parentKey: string, childKey: string) => {
    if (editingField !== parentKey) return;
    setEditValue((prev: any) => {
      // Use empty string as key if childKey is empty
      const key = childKey || '';
      const newObj = { [key]: '', ...(prev || {}) };
      return newObj;
    });
  };

  const updateArrayValue = (parentKey: string, index: number, value: string) => {
    if (editingField !== parentKey) return;
    setEditValue((prev: any) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      arr[index] = value;
      return arr;
    });
  };

  const addArrayItem = (parentKey: string) => {
    if (editingField !== parentKey) return;
    setEditValue((prev: any) => (Array.isArray(prev) ? ['', ...prev] : ['']));
  };

  const removeArrayItem = (parentKey: string, index: number) => {
    if (editingField !== parentKey) return;
    setEditValue((prev: any) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      return arr.filter((_: any, i: number) => i !== index);
    });
  };

  const saveEntry = () => {
    if (!editingField) return;
    
    setEntries(prev => prev.map(entry => 
      entry.key === editingField ? { ...entry, value: editValue } : entry
    ));
    
    setEditingField(null);
    setEditValue(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Category name is required');
      return;
    }

    // Check for empty keys in entries
    for (const entry of entries) {
      if (!entry.key || entry.key.trim() === '') {
        setError('Property key cannot be empty. Please enter a key name for all properties.');
        return;
      }
      
      // Check for empty keys in object properties
      if (typeof entry.value === 'object' && entry.value !== null && !Array.isArray(entry.value)) {
        const emptyKeys = Object.keys(entry.value).filter(key => key.trim() === '');
        if (emptyKeys.length > 0) {
          setError('Object property keys cannot be empty. Please enter key names for all properties.');
          return;
        }
      }
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare entries for all properties
      const batchEntries: Array<{ key: string; value: any }> = [];
      
      for (const entry of entries) {
        if (entry.key && entry.value !== undefined && entry.value !== '') {
          // Key should already be in API format from the modal
          const apiKey = entry.key;
          
          if (typeof entry.value === 'object' && entry.value !== null) {
            // Handle objects and arrays - flatten to individual entries
            if (Array.isArray(entry.value)) {
              // Handle arrays - create entries for each array item
              for (let i = 0; i < entry.value.length; i++) {
                if (entry.value[i] !== '') { // Skip empty items
                  batchEntries.push({
                    key: `${apiKey}.${i}`,
                    value: entry.value[i]
                  });
                }
              }
              } else {
                // Handle objects - create entries for each key-value pair
                for (const [subKey, subValue] of Object.entries(entry.value)) {
                  if (subKey !== '' && subValue !== '') { // Skip empty keys/values
                    batchEntries.push({
                      key: convertNestedKeyToApi(apiKey, subKey),
                      value: subValue
                    });
                  }
                }
              }
          } else {
            // Handle primitive values
            batchEntries.push({
              key: apiKey,
              value: entry.value
            });
          }
        }
      }

      // Create the category with all entries in a single request
      await customerCategoriesService.createCustomerCategory(
        accessToken,
        setUserAndToken,
        {
          name: formData.name.trim(),
          description: formData.description.trim(),
          is_default: formData.is_default,
          entries: batchEntries
        }
      );

      // Navigate back to categories list
      navigate('/customer-categories');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  const renderValue = (key: string, value: any) => {
    if (Array.isArray(value)) {
      return (
        <div className="space-y-2">
          <ul className="space-y-1">
            {value.map((item: string, index: number) => (
              <li key={index} className="flex items-start">
                <span className="text-gray-500 text-sm mr-2">•</span>
                <span className="text-sm text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    } else if (typeof value === 'object' && value !== null) {
      return (
        <div className="space-y-2">
          <div className="overflow-hidden">
            <table className="min-w-full">
              <tbody>
                {Object.entries(value).map(([subKey, subValue]) => (
                  <tr key={subKey} className="border-b border-gray-100 last:border-b-0">
                    <td className="py-2 pr-4 text-sm text-gray-600 font-medium align-top w-1/3">
                      {subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                    </td>
                    <td className="py-2 text-sm text-gray-700 align-top">
                      {subValue as string}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    } else {
      return (
        <div className="text-sm text-gray-700">
          {value}
        </div>
      );
    }
  };

  const renderEditField = (key: string, value: any) => {
    const currentValue = key === editingField ? editValue : value;
    const isNewProperty = key.startsWith('property_');
    
    // For new properties, show name and type selectors
    if (isNewProperty) {
      const currentType = Array.isArray(currentValue) ? 'array' : typeof currentValue === 'object' && currentValue !== null ? 'object' : 'value';
      
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-xs text-slate-600 mb-1">Property Key</label>
              <input
                type="text"
                value={key.replace(/^property_/, '')}
                onChange={(e) => updatePropertyName(key, e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Enter property key"
              />
            </div>
            <div className="w-32">
              <label className="block text-xs text-slate-600 mb-1">Value Type</label>
              <select
                value={currentType}
                onChange={(e) => updatePropertyType(key, e.target.value as 'value' | 'object' | 'array')}
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
              >
                <option value="value">Value</option>
                <option value="object">Object</option>
                <option value="array">Array</option>
              </select>
            </div>
          </div>
          
          {/* Value editing based on type */}
          {currentType === 'value' && (
            <div>
              <label className="block text-xs text-slate-600 mb-1">Value</label>
              <input
                type="text"
                value={currentValue}
                onChange={(e) => updateFormValue(key, e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Enter value"
              />
            </div>
          )}
          
          {currentType === 'object' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-xs text-slate-600">Object Properties</label>
                <AddObjectKeyForm parentKey={key} onAdd={addObjectKey} />
              </div>
              {Object.entries(currentValue).map(([subKey, subValue], index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={subKey}
                    onChange={(e) => {
                      const newObj = { ...currentValue };
                      delete newObj[subKey];
                      newObj[e.target.value] = subValue;
                      updateFormValue(key, newObj);
                    }}
                    className="w-1/3 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                    placeholder="Key"
                  />
                  <input
                    type="text"
                    value={subValue as string}
                    onChange={(e) => updateObjectValue(key, subKey, e.target.value)}
                    className="flex-1 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                    placeholder="Value"
                  />
                  <button
                    type="button"
                    onClick={() => removeObjectKey(key, subKey)}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Remove property"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {currentType === 'array' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-xs text-slate-600">Array Items</label>
                <button
                  type="button"
                  onClick={() => addArrayItem(key)}
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Add Item
                </button>
              </div>
              {currentValue.map((item: string, index: number) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => updateArrayValue(key, index, e.target.value)}
                    className="flex-1 border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
                    placeholder="Array item"
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem(key, index)}
                    className="text-red-500 hover:text-red-700 p-1"
                    title="Remove item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    
    return renderValueContent(key, currentValue);
  };

  const renderValueContent = (key: string, currentValue: any) => {
    if (Array.isArray(currentValue)) {
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Array Items</span>
            <button
              type="button"
              onClick={() => addArrayItem(key)}
              className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
            >
              <Plus className="w-3 h-3" />
              Add Item
            </button>
          </div>
          {currentValue.map((item: string, index: number) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                value={item}
                onChange={(e) => updateArrayValue(key, index, e.target.value)}
                className="flex-1 border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Array item"
              />
              <button
                type="button"
                onClick={() => removeArrayItem(key, index)}
                className="text-red-500 hover:text-red-700 p-1"
                title="Remove item"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      );
    } else if (typeof currentValue === 'object' && currentValue !== null) {
      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-600">Object Properties</span>
            <AddObjectKeyForm parentKey={key} onAdd={addObjectKey} />
          </div>
          {Object.entries(currentValue).map(([subKey, subValue], index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                value={subKey}
                onChange={(e) => {
                  const newObj = { ...currentValue };
                  delete newObj[subKey];
                  newObj[e.target.value] = subValue;
                  updateFormValue(key, newObj);
                }}
                className="w-1/3 border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Key"
              />
              <input
                type="text"
                value={subValue as string}
                onChange={(e) => updateObjectValue(key, subKey, e.target.value)}
                className="flex-1 border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Value"
              />
              <button
                type="button"
                onClick={() => removeObjectKey(key, subKey)}
                className="text-red-500 hover:text-red-700 p-1"
                title="Remove property"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      );
    } else {
      return (
        <input
          type="text"
          value={currentValue ?? ''}
          onChange={(e) => updateFormValue(key, e.target.value)}
          className="w-full border border-slate-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
          placeholder="Enter value"
        />
      );
    }
  };

  // Helper components

  const AddObjectKeyForm: React.FC<{ parentKey: string; onAdd: (parentKey: string, childKey: string) => void }> = ({ parentKey, onAdd }) => {
    const handleAdd = () => {
      // Add with blank key - user will fill it in
      onAdd(parentKey, '');
    };

    return (
      <button
        type="button"
        onClick={handleAdd}
        className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
      >
        <Plus className="w-3 h-3" />
        Add
      </button>
    );
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/customer-categories')}
          className="p-2 hover:bg-slate-100 rounded-lg transition"
          aria-label="Back to categories"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <p className="text-slate-600">Add a new customer category with custom criteria and evaluation rules</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Main Information */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Main Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Category Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="e.g., Premium Account"
                required
              />
            </div>
            
            <div className="flex flex-col justify-end">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_default}
                  onChange={(e) => handleInputChange('is_default', e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-slate-700">Set as default category</span>
              </div>
              <p className="text-xs text-slate-500 ml-6">Default categories will be linked to the new users automatically.</p>
            </div>
          </div>
          
          <div className="mt-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
              placeholder="Describe this customer category and its purpose..."
            />
          </div>
        </div>

        {/* Category Properties */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-900">Properties</h2>
            <button
              type="button"
              onClick={() => setShowAddPropertyModal(true)}
              className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Property
            </button>
          </div>
          
          <div className="p-6 space-y-6">
            {entries.map((entry) => {
              const isEditing = editingField === entry.key;
              
              return (
                <div key={entry.key} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-slate-900">
                      {(() => {
                        const displayKey = entry.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        console.log('Stored key:', entry.key, 'Display key:', displayKey);
                        return displayKey;
                      })()}
                    </h3>
                    <div className="flex items-center space-x-2">
                      {!isEditing ? (
                        <>
                          <button
                            type="button"
                            onClick={() => startEditing(entry.key, entry.value)}
                            className="p-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                            title="Edit this property"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeEntry(entry.key)}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            title="Remove this property"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={saveEntry}
                            className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                            title="Save changes"
                          >
                            <Save className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={cancelEditing}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            title="Cancel editing"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    {isEditing ? renderEditField(entry.key, entry.value) : renderValue(entry.key, entry.value)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <X className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/customer-categories')}
            className="px-6 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 transition"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 rounded-lg bg-[#4a67b7] text-white font-semibold shadow hover:bg-primary/90 transition flex items-center gap-2"
            disabled={loading}
          >
            <Save className="w-4 h-4" />
            {loading ? 'Creating...' : 'Create Category'}
          </button>
        </div>
      </form>

      {/* Add Property Modal */}
      <AddPropertyModal
        isOpen={showAddPropertyModal}
        onClose={() => setShowAddPropertyModal(false)}
        onSave={handleAddProperty}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Property"
        message="Are you sure you want to delete this property? This action cannot be undone."
        itemName={deleteTarget ? deleteTarget.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : undefined}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default CreateCategory;
